<script lang="ts">
import Vue, { CreateElement, VNode } from 'vue'
import { parse } from 'postsvg'

// Типы
interface DeviceChild {
  id?: string
  $id?: string
  cls?: string
  name?: string
  template?: string
  x?: number
  y?: number
  width?: number
  height?: number
  orientation?: number
  side?: string
  mount?: string
  slot_size?: number
  is_broken?: boolean
  is_used?: boolean
  children?: DeviceChild[]
  orient?: string
  [key: string]: any
}

interface TreeNode {
  $id?: string
  cls?: string
  children?: TreeNode[]
  disabled?: boolean
  name?: string
  slot_size?: number
  [key: string]: any
}

interface DeviceData extends DeviceChild {
  $id?: string
  mount?: string
  [key: string]: any
}

interface SelectedDevice {
  $id?: string
  [key: string]: any
}

interface StyleObject {
  width?: string
  height?: string
  position?: string
  top?: string
  left?: string
  backgroundImage?: string
  backgroundSize?: string
  backgroundColor?: string
  [key: string]: any
}

export default Vue.extend({
  name: 'DEVICE_CARD',

  props: {
    data: {
      type: Object as () => DeviceData,
      default: () => ({})
    },
    nodes: {
      type: Array as () => TreeNode[],
      default: () => []
    },
    selected: {
      type: Object as () => SelectedDevice,
      default: () => ({})
    },
    side: {
      type: String,
      default: () => 'front'
    },
    scaleObj: {
      type: Number,
      default: () => 1
    },
    editable: {
      type: Boolean,
      default: () => true
    },
    subscribed: {
      type: Boolean,
      default: false  // По умолчанию функционал изменения цвета отключен
    },
  },

  data(): {
    model: DeviceChild[]
    selectedItem: SelectedDevice | null
    currentBackgroundUrl: string | undefined
    allDevices: Map<string, DeviceChild>
  } {
    return {
      model: [],
      selectedItem: null,
      currentBackgroundUrl: undefined,
      allDevices: new Map<string, DeviceChild>(),
    }
  },

  watch: {
    data: {
      handler(newVal: DeviceData): void {
        if (newVal.$id) {
          this.setup('WATCH data')
        }
      },
      deep: true
    },
    '$store.state.devices.selectedItem': {
      handler(newVal: SelectedDevice): void {
        this.selectedItem = newVal
        this.setup('WATCH selectedItem')
      },
      deep: true
    },
    '$store.state.monitoring.status': {
      handler(newStatus: Record<string, any>): void {
        if (!this.subscribed) {
          // console.log('Status monitoring is disabled (subscribed = false)')
          return
        }
        
        if (!this.data || !this.data.$id) return
        
        const oldStatus = this.$store.state.monitoring.status
        
        // Проверяем, изменился ли статус любого из устройств
        let statusChanged = false
        
        for (const deviceId of this.allDevices.keys()) {
          if (oldStatus[deviceId] !== newStatus[deviceId]) {
            statusChanged = true
            // console.log(`Status changed for device: ${deviceId}`)
            break
          }
        }
        
        if (statusChanged) {
          // Просто обновляем весь SVG
          this.updateDeviceBackground()
        }
      },
      deep: true
    },
    side: {
      handler(): void {
        console.log(this.$options.name, 'side changed to:', this.side)
        this.currentBackgroundUrl = undefined
        if (this.data && this.data.children) {
          this.model = this.parseData(this.data.children)
        }
        // this.updateDeviceBackground()
        this.refreshAllDevicesBackgrounds('watch side')
      }
    },
    scaleObj: {
      handler(): void {
        this.$forceUpdate()
      }
    },
    // Добавляем watcher на subscribed, чтобы перерисовать компонент при изменении подписки
    subscribed: {
      handler(newVal: boolean): void {
        // console.log(this.$options.name, 'subscribed changed to:', newVal)
        // При изменении subscribed перезагружаем фон (с модификацией или без)
        // this.updateDeviceBackground()
      }
    }
  },

  created(): void {
    this.setup('created')
  },

  beforeDestroy() {
    if (this.currentBackgroundUrl && this.currentBackgroundUrl.startsWith('blob:')) {
      URL.revokeObjectURL(this.currentBackgroundUrl)
    }
  },


  methods: {
    setup(caller: string): void {
      // console.log(this.$options.name, 'SETUP', 'caller', caller)
      // console.log(this.$options.name, 'SETUP', 'data', this.data)
      // console.log(this.$options.name, 'SETUP', 'nodes', this.nodes)
      
      // Очищаем Map перед сбором новых
      this.allDevices.clear()
      
      if (this.nodes[0]?.$id) {
        this.allDevices.set(this.nodes[0].$id, this.nodes[0])
        
        if (this.nodes[0].children) {
          this.model = this.parseData(this.nodes[0].children)
        }
      }
      else if (this.data && this.data.$id) {
        this.allDevices.set(this.data.$id, this.data)
        
        if (this.data.children) {
          this.model = this.parseData(this.data.children)
        }
      }
      
      // Принудительно обновляем фоны всех устройств
      this.refreshAllDevicesBackgrounds('setup')
    },

    deviceClickHandler(e: MouseEvent): void {
      if (!e) return
      
      const target = e.target as HTMLElement
      if (target.className === 'parent-class' || target.className === 'parent-class-active') {
        this.$emit('select', null)
      } else {
        if (target.className === 'slot' || target.className === 'subslot') {
          return
        } else {
          this.$emit('select', target.id)
        }
      }
    },

    getBackgroundUrl(data: DeviceChild): string | undefined {
      if (data.template === undefined) return undefined
      
      let side: string | undefined
      
      if (data.cls === 'device') {
        if (data.mount) {
          side = data.mount === this.side ? 'front' : 'rear'
        } else {
          side = this.side
        }
      } else {
        const side$ = this.checkSide(data.side)
        if (side$ === this.side) {
          side = 'image'
        }
      }
      
      const $id = `${data.cls}$${data.id}`
      if (side) {
        const path = this.$bridge.getImageLink($id, side)
        return path.replace(/\s/g, '%20')
      }
      return undefined
    },

    getSignalLightColor(data: DeviceChild): string {
      // Если подписка отключена - возвращаем цвет по умолчанию (Прозрачный)
      if (!this.subscribed) {
        // console.log('Status monitoring disabled, using default color')
        return 'transparent' // Прозрачный цвет
      }
      
      const status = this.$store.state.monitoring.status[data.$id]
      return this.$getColor(status)
    },

    /**
     * Модифицирует SVG строку, заменяя fill у всех path внутри тегов с id="status_light_*"
     * @param svgString - исходная SVG строка
     * @param getColorForSuffix - функция, которая по суффиксу возвращает цвет
     */
    modifySVGSignalLight__(svgString: string, getColorForSuffix: (suffix: string) => string): string {
      if (!svgString) return svgString
      
      let modified = svgString
      let matchCount = 0
      
      // Ищем парные теги с id="status_light_*"
      const pairedRegex = /<([a-zA-Z][a-zA-Z0-9]*)[^>]*id=["']status_light(?:_([^"']+))?["'][^>]*>([\s\S]*?)<\/\1>/gi
      
      modified = modified.replace(pairedRegex, (fullTag, tagName, suffix, innerContent) => {
        matchCount++
        const color = getColorForSuffix(suffix || 'default')
        
        let modifiedFullTag = fullTag
        let modifiedInner = innerContent
        
        modifiedFullTag = modifiedFullTag.replace(/fill="#[A-Fa-f0-9]+"/g, `fill="${color}"`)
        modifiedFullTag = modifiedFullTag.replace(/fill='#[A-Fa-f0-9]+'/g, `fill='${color}'`)
        modifiedInner = modifiedInner.replace(/fill="#[A-Fa-f0-9]+"/g, `fill="${color}"`)
        modifiedInner = modifiedInner.replace(/fill='#[A-Fa-f0-9]+'/g, `fill='${color}'`)
        
        const newFullTag = modifiedFullTag.replace(innerContent, modifiedInner)       
        return newFullTag
      })
      
      // Ищем самозакрывающиеся теги с id="status_light_*"
      const selfClosingRegex = /<([a-zA-Z][a-zA-Z0-9]*)[^>]*id=["']status_light(?:_([^"']+))?["'][^>]*\/>/gi
      
      modified = modified.replace(selfClosingRegex, (fullTag, tagName, suffix) => {
        matchCount++
        const color = getColorForSuffix(suffix || 'default')
        
        // Заменяем fill в самозакрывающемся теге
        let modifiedTag = fullTag
        modifiedTag = modifiedTag.replace(/fill="#[A-Fa-f0-9]+"/g, `fill="${color}"`)
        modifiedTag = modifiedTag.replace(/fill='#[A-Fa-f0-9]+'/g, `fill='${color}'`)
        
        return modifiedTag
      })
      
      if (matchCount === 0) {
        console.warn('⚠️ No status_light elements found in SVG')
      } else {
        // console.log(`✅ Modified ${matchCount} status_light element(s) in SVG`)
      }
      
      return modified
    },

    modifySVGSignalLight(svgString: string, getColorForSuffix: (suffix: string) => string): string {
      if (!svgString) return svgString
      
      let modified = svgString
      let matchCount = 0
      
      // Вспомогательная функция для замены fill и fill-opacity
      const modifyTag = (tag: string, color: string): string => {
        let modifiedTag = tag
        // Заменяем fill
        modifiedTag = modifiedTag.replace(/fill="#[A-Fa-f0-9]+"/g, `fill="${color}"`)
        modifiedTag = modifiedTag.replace(/fill='#[A-Fa-f0-9]+'/g, `fill='${color}'`)
        // Заменяем или добавляем fill-opacity
        if (modifiedTag.includes('fill-opacity=')) {
          modifiedTag = modifiedTag.replace(/fill-opacity="[^"]*"/g, 'fill-opacity="1"')
          modifiedTag = modifiedTag.replace(/fill-opacity='[^']*'/g, "fill-opacity='1'")
        } else {
          // Добавляем fill-opacity после fill или в конец открывающего тега
          if (modifiedTag.includes('fill=')) {
            modifiedTag = modifiedTag.replace(/(fill="[^"]*")/, `$1 fill-opacity="1"`)
            modifiedTag = modifiedTag.replace(/(fill='[^']*')/, `$1 fill-opacity='1'`)
          } else {
            // Если нет fill, добавляем в конец открывающего тега перед >
            modifiedTag = modifiedTag.replace(/\/?>/, ` fill-opacity="1"$&`)
          }
        }
        return modifiedTag
      }
      
      // Ищем парные теги с id="status_light_*"
      const pairedRegex = /<([a-zA-Z][a-zA-Z0-9]*)[^>]*id=["']status_light(?:_([^"']+))?["'][^>]*>([\s\S]*?)<\/\1>/gi
      
      modified = modified.replace(pairedRegex, (fullTag, tagName, suffix, innerContent) => {
        matchCount++
        const color = getColorForSuffix(suffix || 'default')
        
        let modifiedFullTag = fullTag
        let modifiedInner = innerContent
        
        modifiedFullTag = modifyTag(modifiedFullTag, color)
        modifiedInner = modifiedInner.replace(/fill="#[A-Fa-f0-9]+"/g, `fill="${color}"`)
        modifiedInner = modifiedInner.replace(/fill='#[A-Fa-f0-9]+'/g, `fill='${color}'`)
        // Добавляем fill-opacity во внутренние элементы
        modifiedInner = modifiedInner.replace(/fill-opacity="[^"]*"/g, 'fill-opacity="1"')
        modifiedInner = modifiedInner.replace(/fill-opacity='[^']*'/g, "fill-opacity='1'")
        
        const newFullTag = modifiedFullTag.replace(innerContent, modifiedInner)       
        return newFullTag
      })
      
      // Ищем самозакрывающиеся теги с id="status_light_*"
      const selfClosingRegex = /<([a-zA-Z][a-zA-Z0-9]*)[^>]*id=["']status_light(?:_([^"']+))?["'][^>]*\/>/gi
      
      modified = modified.replace(selfClosingRegex, (fullTag, tagName, suffix) => {
        matchCount++
        const color = getColorForSuffix(suffix || 'default')
        return modifyTag(fullTag, color)
      })
      
      if (matchCount === 0) {
        console.warn('⚠️ No status_light elements found in SVG')
      } else {
        // console.log(`✅ Modified ${matchCount} status_light element(s) in SVG`)
      }
      
      return modified
    },

    async loadModifiedSVG(url: string, getColorForSuffix: (suffix: string) => string): Promise<string> {
      try {
        const response = await fetch(url)
        let svgText = await response.text()
        
        if (this.subscribed) {
          svgText = this.modifySVGSignalLight(svgText, getColorForSuffix)
        }
        
        // Вместо Data URL используем Blob URL
        const blob = new Blob([svgText], { type: 'image/svg+xml' })
        const blobUrl = URL.createObjectURL(blob)

        return blobUrl
      } catch (error) {
        console.error('🔴 Error loading SVG:', error, url)
        return url
      }
    },

    async getAsyncBackground(elem: DeviceChild): Promise<string | undefined> {

      const originalUrl = this.getBackgroundUrl(elem)
      if (!originalUrl) {
        // console.log('🔴 No original URL for:', elem.$id)
        return undefined
      }

      // Создаем функцию, которая по суффиксу возвращает цвет
      const getColorForSuffix = (suffix: string): string => {
        // console.log('🎨 getColorForSuffix called with suffix:', suffix)
        
        // Выведем все доступные имена устройств для отладки
        const availableDevices = Array.from(this.allDevices.values())
        
        let targetDevice: DeviceChild | undefined
        
        // Пробуем разные варианты поиска
        for (const device of availableDevices) {
          // По точному совпадению name
          if (device.name === suffix) {
            // console.log('🎨 Found by exact name match')
            targetDevice = device
            break
          }
          // По частичному совпадению (например, "PSU1" содержит "PSU")
          if (device.name && device.name.includes(suffix)) {
            // console.log('🎨 Found by partial name match')
            targetDevice = device
            break
          }
          // По id (без префикса)
          if (device.id === suffix) {
            // console.log('🎨 Found by id match')
            targetDevice = device
            break
          }
          // По $id (без префикса)
          if (device.$id === suffix) {
            // console.log('🎨 Found by $id match')
            targetDevice = device
            break
          }
        }
        
        if (!targetDevice) {
          // console.warn(`⚠️ Device not found for suffix: ${suffix}, using transparent`)
          return 'transparent'
        }
        
        const status = this.$store.state.monitoring.status[targetDevice.$id]
        const color = this.$getColor(status)
        //Пока решили красить только "плохие" статусы
        if(status == 'alarm' || status == 'warning' || status == 'damage') {
          // console.log('getColorForSuffix return color:', color, ' of status:', status)
          return color
        }
        else {
          // console.log('getColorForSuffix return transparent for status:', status)
          return 'transparent'
        }
        
      }
      const result = await this.loadModifiedSVG(originalUrl, getColorForSuffix)     
      return result
    },

    async updateDeviceBackground(): Promise<void> {
       
      if (!this.data) {
        // console.log('🔴 No data, skipping')
        return
      }
      
      let isVisible = false
      
      if (this.data.cls === 'device') {
        if (this.data.mount) {
          isVisible = this.data.mount === this.side
        } else {
          isVisible = true
        }
      } else {
        const side$ = this.checkSide(this.data.side)
        isVisible = side$ === this.side
      }

      const bgUrl = await this.getAsyncBackground(this.data)
      
      if (bgUrl) {
        if (this.currentBackgroundUrl && this.currentBackgroundUrl.startsWith('blob:')) {
          URL.revokeObjectURL(this.currentBackgroundUrl)
        }
        this.currentBackgroundUrl = bgUrl

        // Проверяем DOM
        const element = document.getElementById(`${this.data.cls}$${this.data.id}`)
        if (element) {
          const bgImage = getComputedStyle(element).backgroundImage
          if (bgImage.includes('data:image')) {
          } else {
          }
        }
      } else {
        // console.log('🔴 bgUrl is falsy, not updating')
      }
    },

    async refreshAllDevicesBackgrounds(caller: string): Promise<void> {
      // console.log('refreshAllDevicesBackgrounds caller', caller)
      if (!this.subscribed) {
        return
      }
      
      // обновляем фон родительского устройства
      await this.updateDeviceBackground()
    },

    checkSide(side$?: string): string | undefined {
      if (this.data.mount === 'front') {
        return side$
      } else {
        return side$ === 'front' ? 'rear' : 'front'
      }
    },

    mapChildren(item: DeviceChild): DeviceChild {
      const parent$ = item     
      // Сохраняем объект в Map
      if (item.$id) {
        this.allDevices.set(item.$id, item)
      }
      
      item.children?.forEach(child => {
        // Сохраняем объект дочернего элемента в Map
        if (child.$id) {
          this.allDevices.set(child.$id, child)
        }
        
        if (!child.width) {
          child.x = item.x
          child.y = item.y
          
          if (item.orientation == 1 || item.orientation == 3) {
            child.height = item.width
            child.width = item.height
          } else {
            child.height = item.height
            child.width = item.width
          }
        }
        
        if (item.orientation) {
          child.orient = `rotate-${item.orientation}`
        }
        
        if (!child.side) {
          child.side = parent$.side
        }
        
        if (child.children?.length) {
          this.mapChildren(child)
        }
      })
      
      return item
    },

    parseData(arr: DeviceChild[]): DeviceChild[] {
      const clonedArr = JSON.parse(JSON.stringify(arr))
      
      clonedArr.forEach(item => {
        if (item.children?.length) {
          this.mapChildren(item)
        } else {
          // Для элементов без детей тоже сохраняем объект
          if (item.$id) {
            this.allDevices.set(item.$id, item)
          }
        }
      })
      
      // console.log('Collected devices:', this.allDevices)
      return clonedArr
    },

    getListeners(elem: DeviceChild): Record<string, (e: MouseEvent) => void> {
      const side$ = this.checkSide(elem.side)
      
      if (this.side === side$) {
        return {
          click: (e: MouseEvent) => {
            e.stopPropagation()
            this.deviceClickHandler(e)
          },
        }
      } else {
        return {
          click: (e: MouseEvent) => {
            e.stopPropagation()
          },
        }
      }
    },

    getClass(elem: DeviceChild): string[] | undefined {
      const side$ = this.checkSide(elem.side)
      if (this.side !== side$) return undefined
      
      const classList: string[] = []
      
      if (this.editable) {
        classList.push(elem.cls || '')
      }
      
      if (elem.orient) {
        classList.push(elem.orient)
      }
      
      if (elem.is_broken) {
        classList.push('broken')
      }
      
      if (elem.is_used) {
        classList.push('used')
      }
      
      if (elem.$id && this.selectedItem && elem.$id === this.selectedItem.$id) {
        classList.push('active')
      }
      
      return classList
    },

    getStyle(elem: DeviceChild): StyleObject {
      const obj: StyleObject = {
        width: `${elem.width}px`,
        height: `${elem.height}px`,
        position: elem.cls === 'module' ? 'fixed' : 'absolute',
      }
      
      if (elem.cls !== 'module') {
        obj.top = `${elem.y}px`
        obj.left = `${elem.x}px`
      }
      
      // ✅ Добавляем условие для device (корневой элемент)
      if (elem.cls === 'device' || elem.cls === 'module' || elem.cls === 'submodule') {
        if (elem.$id === this.data.$id && this.currentBackgroundUrl) {
          obj.backgroundImage = `url(${this.currentBackgroundUrl})`
        } else {
          const side$ = this.checkSide(elem.side)
          if (side$ === this.side) {
            const bgUrl = this.getBackgroundUrl(elem)
            if (bgUrl) {
              obj.backgroundImage = `url(${bgUrl})`
            }
          }
        }
        
        if (elem.slot_size == 1) {
          obj.backgroundSize = '100% 100%'
        }
      }
      
      if (this.selectedItem && elem.$id !== this.selectedItem.$id && elem.cls !== 'module') {
        obj.backgroundColor = 'rgba(255,255,255,0.7)'.replace(/[^,]+(?=\))/, '0.5')
      }
      
      return obj
    },
  },

  render(createElement: CreateElement): VNode | undefined {
    const getData = (arr: DeviceChild[]): VNode[] => {
      if (!arr.length) return []
      
      const vDom: VNode[] = []
      
      arr.forEach(elem => {
        if (elem.cls === 'port') return
        
        const side$ = this.checkSide(elem.side)
        if (this.side !== side$) return
        
        const tag = 'div'
        const options = {
          attrs: {
            id: `${elem.cls}$${elem.id}`,
          },
          class: this.getClass(elem),
          style: this.getStyle(elem),
          on: this.getListeners(elem),
        }
        
        let inner: VNode[] = []
        if (elem.children?.length) {
          const childInner = getData(elem.children)
          inner.push(...childInner)
        }
        
        const child = createElement(tag, options, inner)
        vDom.push(child)
      })
      
      return vDom
    }

    const vDom = getData(this.model)

    if (this.data) {
      // console.log('🎨 RENDER - currentBackgroundUrl:', this.currentBackgroundUrl ? 'exists (starts with ' + this.currentBackgroundUrl.substring(0, 50) + ')' : 'undefined')
      return createElement(
        'div',
        {
          class: this.selectedItem && this.selectedItem.$id === this.data.$id 
            ? 'parent-class-active' 
            : 'parent-class',
          attrs: { id: `${this.data.cls}$${this.data.id}` },
          domProps: { draggable: 'true' },
          style: {
            transform: `scale(${this.scaleObj})`,
            width: `${this.data.width}px`,
            height: `${this.data.height}px`,
            top: '0px',
            left: '0px',
            backgroundImage: this.currentBackgroundUrl 
              ? `url(${this.currentBackgroundUrl})` 
              : `url(${this.getBackgroundUrl(this.data)})`,
            position: 'relative',
          },
          on: {
            click: this.deviceClickHandler,
          },
        },
        vDom
      )
    }
    
    return undefined
  },
})
</script>

<style scoped>
.parent-class {
  opacity: 1;
  z-index: 2;
  transform-origin: 50% 0%;
}

.parent-class-active {
  outline: solid 2px orange;
  opacity: 1;
  z-index: 2;
  transform-origin: 50% 0%;
}

.active {
  outline: solid 1px orange;
}

.parent-class:hover {
  background-color: transparent;
}

.module {
  z-index: 2;
}

:hover {
  opacity: 1
}

.rotate-1 {
  transform-origin: 0 0;
  transform: rotate(90deg)translate(0, -100%);
}

.rotate-2 {
  transform-origin: 0 0;
  transform: rotate(180deg)translate(0, 0);
}

.rotate-3 {
  transform-origin: 0 0;
  transform: rotate(270deg)translate(-100%, 0);
}

.broken {
  background-color: red;
}

.used {
  box-sizing: content-box;
  border: solid 1px #F7941D;
  opacity: 1;
}
</style>